import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlTableState from '../../../hooks/useUrlTableState';
import {
  Clock,
  Calendar,
  Search,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  FileText,
  User,
  MapPin,
  Timer,
  Plus,
  Edit,
  Trash2,
  Building,
  X,
  Mail,
  Users,
  UserCheck,
  UserX,
  AlarmClock,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Upload,
  Home
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { formatDate, formatTime } from "../../../utils/stringUtils";
import { toast } from '../../../utils/toast';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import WarningPopup from "../../../components/WarningPopup";
import BulkUploadModal from "../../../components/common/BulkUploadModal";
import RegularizationRequestModal from "../../../components/hrms/RegularizationRequestModal";

const getStatusLabel = (status) => {
  return (status || 'absent')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const parseHMSToSeconds = (timeStr) => {
  if (!timeStr || timeStr === 'N/A') return -1;
  const parts = String(timeStr).split(':').map(Number);
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return -1;
  const hours = parts[0];
  const minutes = parts[1];
  const seconds = Number.isNaN(parts[2]) ? 0 : parts[2];
  return hours * 3600 + minutes * 60 + seconds;
};

const parseWorkingHoursToMinutes = (workingHours) => {
  if (!workingHours || workingHours === 'N/A') return -1;
  const match = String(workingHours).match(/(\d+)\s*h\s*(\d+)\s*m/i);
  if (!match) return -1;
  return Number(match[1]) * 60 + Number(match[2]);
};

const getShiftDetails = (log) => {
  const shift = log?.shiftDetails || log?.shift || {};
  return {
    startTime: shift.siftStartTime || shift.shiftStartTime || shift.startTime || 'N/A',
    endTime: shift.siftEndTime || shift.shiftEndTime || shift.endTime || 'N/A',
    type: shift.shiftName || shift.name || shift.shiftType || shift.type || 'Regular',
    graceCheckInTime: Number(shift.graceCheckInTime || 0),
    graceDays: Number(shift.graceDays || 0),
    graceDaysTime: Number(shift.graceDaysTime || shift.graceDaysTiming || 0)
  };
};

// Helper to format ordinal number with suffix (1 -> 1st Grace, 2 -> 2nd Grace, 3 -> 3rd Grace, etc.)
const getOrdinalGraceLabel = (num) => {
  if (!num) return '';
  const j = num % 10;
  const k = num % 100;
  let suffix = 'th';
  if (j === 1 && k !== 11) suffix = 'st';
  else if (j === 2 && k !== 12) suffix = 'nd';
  else if (j === 3 && k !== 13) suffix = 'rd';
  return `${num}${suffix} Grace`;
};

const getShiftLabel = (log) => {
  const shift = getShiftDetails(log);
  const timeRange = `${formatTime(shift.startTime)} - ${formatTime(shift.endTime)}`;
  return `${shift.type || 'Regular'} (${timeRange})`;
};

/** Weekday label for attendance date strings (YYYY-MM-DD) without timezone drift. */
const getDayNameFromDate = (dateValue) => {
  if (!dateValue) return '';

  const dateStr = String(dateValue).split('T')[0];
  const parts = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (parts) {
    const [, year, month, day] = parts;
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(dateObj.getTime())) {
      return dateObj.toLocaleDateString('en-IN', { weekday: 'long' });
    }
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-IN', { weekday: 'long' });
};

const AttendanceLogs = ({ onBack, userRole }) => {
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: attendanceFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateAttendanceFilter,
    updatePage: updateAttendancePage,
    updateLimit: updateAttendanceLimit,
    resetFilters: resetAttendanceFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterStatus = attendanceFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updateAttendanceFilter({ status });
  }, [updateAttendanceFilter]);

  const filterStartDate = attendanceFilters.startDate || '';
  const filterEndDate = attendanceFilters.endDate || '';

  const setDateRange = useCallback((range) => {
    updateAttendanceFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateAttendanceFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateAttendanceFilter({ startDate });
  }, [updateAttendanceFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateAttendanceFilter({ endDate });
  }, [updateAttendanceFilter]);

  const currentPage = attendanceFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateAttendancePage(page);
  }, [updateAttendancePage]);

  const itemsPerPage = attendanceFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateAttendanceLimit(limit);
  }, [updateAttendanceLimit]);

  const [employees, setEmployees] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [regularizationPrefill, setRegularizationPrefill] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    employee: "",
    date: "",
    clockIn: "",
    clockOut: "",
    status: "present",
    remarks: "",
  });



  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // React Query: Search Employees (fetch when dropdown is open, load initial if search empty)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['attendance-logs-employees-search', debouncedEmployeeSearch],
    queryFn: async ({ signal }) => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50`;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: 'GET',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen, // Call when search dropdown is open
    staleTime: 0,
  });

  // React Query: Load Attendance Logs with filters and pagination
  const { data: attendanceLogsData, isLoading, refetch: refetchAttendanceLogs } = useQuery({
    queryKey: ['attendance-logs', currentPage, itemsPerPage, searchTerm, filterStatus, filterStartDate, filterEndDate],
    queryFn: async ({ signal }) => {
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", itemsPerPage.toString());
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (filterStartDate) queryParams.append("startDate", filterStartDate);
      if (filterEndDate) queryParams.append("endDate", filterEndDate);
      // Changed from "name" to "search" for biometric API
      if (searchTerm) queryParams.append("search", searchTerm);

      // NEW: Biometric Attendance API (synced from MSSQL)
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: "GET",
        signal,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        return data.data;
      } else {
        throw new Error(data.responseMessage || 'Failed to load attendance logs');
      }
    },
    staleTime: 0,
    refetchOnMount: true,
  });

  // React Query: Fetch Attendance Stats (KPI Cards)
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['attendance-stats'],
    queryFn: async ({ signal }) => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/stats`;
      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: "GET",
        signal,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.data;
      }
      return { totalEmployees: 0, presentToday: 0, lateToday: 0, absentToday: 0 };
    },
    staleTime: 0,
  });

  // React Query: Delete Attendance Log Mutation
  const deleteLogMutation = useMutation({
    mutationFn: async (logId) => {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/${logId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete attendance log');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance-logs'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
    },
  });


  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when modal closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showCreateModal && !showEditModal) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showCreateModal, showEditModal]);

  // When editing, fetch employee data if employee ID is set but employee data is missing
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchEmployeeForEdit = async () => {
      if (showEditModal && formData.employee && !selectedEmployeeData) {
        try {
          const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(formData.employee)}`;
          const token = localStorage.getItem("auth_token");
          const response = await fetch(url, {
            method: 'GET',
            signal,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.responseCode === 200) {
              const employeesArr = data.data.employees || [];
              // Try to find exact match by _id
              const employee = employeesArr.find((emp) => emp._id === formData.employee || emp.employeeId === formData.employee);
              if (employee) {
                setSelectedEmployeeData(employee);
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error("Error fetching employee for edit:", error);
          }
        }
      }
    };

    fetchEmployeeForEdit();

    return () => {
      controller.abort();
    };
  }, [showEditModal, formData.employee, selectedEmployeeData]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 ml-1 text-brand" /> :
      <ChevronDown className="h-3 w-3 ml-1 text-brand" />;
  };

  // Extract logs and pagination from query data
  const attendanceLogsRaw = attendanceLogsData?.logs || [];

  // Process logs: Derive check-in and check-out strictly from the punches timeline
  const processedLogs = useMemo(() => {
    return attendanceLogsRaw.map(log => {
      const originalPunches = log.punches || [];

      // Sort punches chronologically by EDateTime if possible
      const sortedPunches = [...originalPunches].sort((a, b) => {
        if (a.EDateTime && b.EDateTime) return new Date(a.EDateTime) - new Date(b.EDateTime);
        return (a.time || '').localeCompare(b.time || '');
      });

      return {
        ...log,
        checkIn: log.checkIn || 'N/A',
        checkOut: log.checkOut || 'N/A',
        workingHours: log.workingHours || 'N/A',
        shiftDetails: getShiftDetails(log),
        punches: sortedPunches
      };
    });
  }, [attendanceLogsRaw]);

  // attendanceLogs are sorted based on user selection; grace day indices are pre-computed by backend on the summary doc

  const attendanceLogs = useMemo(() => {
    let sortableLogs = [...processedLogs];
    if (sortConfig.key !== null) {
      sortableLogs.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'employee':
            aValue = (a.employeeName || '').toLowerCase();
            bValue = (b.employeeName || '').toLowerCase();
            break;
          case 'date':
            aValue = a.date || '';
            bValue = b.date || '';
            break;
          case 'checkIn':
            aValue = a.checkIn || '00:00';
            bValue = b.checkIn || '00:00';
            break;
          case 'checkOut':
            aValue = a.checkOut || '00:00';
            bValue = b.checkOut || '00:00';
            break;
          case 'workingHours':
            aValue = parseWorkingHoursToMinutes(a.workingHours);
            bValue = parseWorkingHoursToMinutes(b.workingHours);
            break;
          case 'shift':
            aValue = getShiftLabel(a).toLowerCase();
            bValue = getShiftLabel(b).toLowerCase();
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          default:
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableLogs;
  }, [processedLogs, sortConfig]);
  const apiPagination = attendanceLogsData?.pagination || {};
  const pagination = {
    currentPage: apiPagination.currentPage || 1,
    totalPages: apiPagination.totalPages || 1,
    totalLogs: apiPagination.totalLogs || 0,
    hasNextPage: typeof apiPagination.hasNextPage === 'boolean'
      ? apiPagination.hasNextPage
      : (apiPagination.currentPage || 1) < (apiPagination.totalPages || 1),
    hasPrevPage: typeof apiPagination.hasPrevPage === 'boolean'
      ? apiPagination.hasPrevPage
      : (apiPagination.currentPage || 1) > 1,
    limit: apiPagination.limit || itemsPerPage || 10
  };



  const currentPageLogIds = useMemo(
    () =>
      attendanceLogs
        .map((log) => {
          const raw = log?.id ?? log?.logId;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [attendanceLogs]
  );

  const exportSortBy = useMemo(() => {
    const keyMap = {
      employee: 'employeename',
      date: 'date',
      checkIn: 'checkin',
      checkOut: 'checkout',
      workingHours: 'workinghours',
      status: 'status',
    };
    return sortConfig.key ? (keyMap[sortConfig.key] || 'date') : 'date';
  }, [sortConfig.key]);

  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterStatus, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageLogIds)
          : [];

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'attendance-logs',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: exportSortBy,
            sortOrder: sortConfig.direction || 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `attendance-logs-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      currentPageLogIds,
      exportSortBy,
      sortConfig.direction,
    ]
  );

  const handleAttendanceLogsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected records to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} record(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleAttendanceLogsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const totalFiltered = Number(pagination?.totalLogs ?? 0);

        if (scope === 'currentPage' && !attendanceLogs.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No attendance logs match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          const allFilters = {};
          if (filterStatus !== 'all') allFilters.status = filterStatus;
          if (searchTerm) allFilters.search = searchTerm;
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: allFilters,
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageLogIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      attendanceLogs.length,
      pagination?.totalLogs,
      baseExportFilters,
      filterStatus,
      searchTerm,
      requestBackendExport,
      itemsPerPage,
      currentPageLogIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "present":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "late":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case "missed":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "yet_checkout":
        return <Timer className="h-4 w-4 text-indigo-500" />;
      case "early_left":
        return <AlarmClock className="h-4 w-4 text-orange-500" />;
      case "absent":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "present":
        return "bg-green-100 text-green-800";
      case "late":
        return "bg-yellow-100 text-yellow-800";
      case "missed":
        return "bg-orange-100 text-orange-800";
      case "pending":
        return "bg-blue-100 text-blue-800";
      case "yet_checkout":
        return "bg-indigo-100 text-indigo-800";
      case "early_left":
        return "bg-orange-100 text-orange-800";
      case "absent":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleViewDetails = async (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };


  if (showDetails && selectedLog) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(false)}
              className="hover:bg-blue-100/60 rounded-full p-2 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Back to attendance logs"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-bold text-gray-800 tracking-tight">
              Attendance Details
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-brand bg-blue-50/30 px-3 py-1.5 rounded-lg border border-blue-100">
            <Calendar className="h-4 w-4" />
            <span className="font-medium">
              {selectedLog.date ? formatDate(selectedLog.date, { includeTime: false }) : 'N/A'}
            </span>
          </div>
        </div>

        <div className="p-6 bg-blue-50/10 space-y-6">

          {/* Section 1: Employee Details (Row Wise) */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-blue-100 pb-2">
              Employee Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <span className="text-xs text-gray-400 block mb-1">Name</span>
                <div className="font-bold text-gray-900 capitalize text-lg break-words">
                  {selectedLog.employeeName || 'Unknown'}
                </div>
                <div className="text-xs text-gray-500 capitalize">{selectedLog.designation || 'Employee'}</div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Department</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 capitalize break-words">
                  <Building className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeDepartment || 'N/A'}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Email</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 break-all">
                  <Mail className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeEmail || 'N/A'}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Employee ID</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 break-all">
                  <User className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeShortId || selectedLog.employeeId || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {(() => {
            const status = selectedLog.status || 'absent';
            const statusLabel = getStatusLabel(status);
            const statusColorInfo = {
              present: "bg-green-50 border-green-200 text-green-700",
              late: "bg-yellow-50 border-yellow-200 text-yellow-700",
              missed: "bg-orange-50 border-orange-200 text-orange-700",
              pending: "bg-blue-50 border-blue-200 text-[#0B2033]",
              yet_checkout: "bg-indigo-50 border-indigo-200 text-indigo-700",
              early_left: "bg-orange-50 border-orange-200 text-orange-700",
              absent: "bg-red-50 border-red-200 text-red-700",
            }[status] || "bg-gray-50 border-gray-200 text-gray-700";

            return (
              <div className={`rounded-2xl p-4 border ${statusColorInfo} flex flex-col md:flex-row items-start md:items-center justify-between shadow-sm gap-4`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg border border-white/70 bg-white/70">
                    {getStatusIcon(status)}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Attendance Status</p>
                    <h4 className="text-lg font-bold capitalize">{statusLabel}</h4>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(selectedLog.isGrace || selectedLog.freeLateNum || selectedLog.graceIndex) && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                      {getOrdinalGraceLabel(selectedLog.freeLateNum || selectedLog.graceIndex)}
                    </span>
                  )}
                  {selectedLog.checkinLate && selectedLog.checkinLateTime && (
                    <span className="px-3 py-1 bg-white/60 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Late by {selectedLog.checkinLateTime}
                    </span>
                  )}
                  {selectedLog.checkoutEarly && selectedLog.checkoutEarlyTime && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Early Left by {selectedLog.checkoutEarlyTime}
                    </span>
                  )}
                </div>
              </div>
            )
          })()}

          {(() => {
            const selectedShift = getShiftDetails(selectedLog);

            return (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2 flex items-center gap-2">
                  <AlarmClock className="h-4 w-4 text-gray-500" />
                  Scheduled Shift
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <span className="text-xs text-gray-400 block mb-1">Shift Type</span>
                    <div className="font-bold text-gray-900 capitalize">{selectedShift.type || "Regular"}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block mb-1">Start Time</span>
                    <div className="font-bold text-gray-900">{formatTime(selectedShift.startTime)}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block mb-1">End Time</span>
                    <div className="font-bold text-gray-900">{formatTime(selectedShift.endTime)}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Section 3: Time Logs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* KPI-style cards for time logs */}
            {(() => {
              const checkOutColorClass = selectedLog.checkoutEarly ? "bg-red-600" : "bg-purple-600";

              const KpiTimeCard = ({ icon: IconCmp, title, value, colorClass }) => (
                <div className="relative overflow-hidden bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full ${colorClass} opacity-5 transition-opacity duration-300`} />
                  <div className="relative z-10 flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <IconCmp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</span>
                      <div className="text-xl font-bold text-gray-900 mt-1">{value}</div>
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  <KpiTimeCard
                    icon={Clock}
                    title="Check In"
                    value={formatTime(selectedLog.checkIn)}
                    colorClass="bg-brand"
                  />
                  <KpiTimeCard
                    icon={Clock}
                    title="Check Out"
                    value={formatTime(selectedLog.checkOut)}
                    colorClass={checkOutColorClass}
                  />
                  <KpiTimeCard
                    icon={Timer}
                    title="Working Hours"
                    value={selectedLog.workingHours || "N/A"}
                    colorClass="bg-green-600"
                  />
                </>
              );
            })()}
          </div>

          {/* Section 4: All Punches Timeline */}
          {selectedLog.punches && selectedLog.punches.length > 0 && (
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                Punch Timeline ({selectedLog.punches.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {selectedLog.punches.map((punch, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === selectedLog.punches.length - 1;
                  const isOut = punch.ioType === "1";

                  return (
                    <div
                      key={`punch-${idx}`}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${isFirst ? 'bg-blue-50 border-blue-200' :
                        isLast ? 'bg-purple-50 border-purple-200' :
                          'bg-gray-50 border-gray-100'
                        }`}
                    >
                      <div className={`p-2 rounded-lg ${isOut ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-[#0F2942]'}`}>
                        <Clock className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-400">
                          {isOut ? 'OUT' : 'IN'} #{idx + 1}
                        </span>
                        <span className={`text-sm font-bold ${isOut ? 'text-purple-700' : 'text-[#0B2033]'}`}>
                          {formatTime(punch.time)}
                        </span>
                      </div>
                      {(isFirst || isLast) && (
                        <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${isFirst ? 'bg-blue-200 text-[#0B2033]' : 'bg-purple-200 text-purple-700'}`}>
                          {isFirst ? 'First' : 'Last'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section 5: Notes (Row Wise) */}
          {selectedLog.notes && (
            <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
              <h5 className="text-sm font-bold text-yellow-800 mb-2 flex items-center gap-2 uppercase tracking-wider">
                <FileText className="h-4 w-4" /> Notes / Remarks
              </h5>
              <p className="text-sm text-yellow-800/90 leading-relaxed bg-white/50 p-3 rounded-lg border border-yellow-100/50">
                {selectedLog.notes}
              </p>
            </div>
          )}

        </div>
      </div>
    );
  }

  const handleSelectAll = (e) => {
    const currentPageIds = attendanceLogs.map(log => log.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteLog = (log) => {
    setLogToDelete(log);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleOpenRegularizeModal = async (log) => {
    try {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(log.employeeId)}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.responseCode === 200 && data.data.employees?.length > 0) {
        const match = data.data.employees.find(emp => emp.employeeId === log.employeeId) || data.data.employees[0];
        
        setRegularizationPrefill({
          employeeId: match._id,
          employeeName: `${match.personalInfo?.firstName || ""} ${match.personalInfo?.lastName || ""}`,
          employeeCode: match.employeeId,
          employeeDepartment: match.employmentInfo?.department,
          employeeData: match,
          date: log.date,
          checkIn: log.checkIn,
          checkOut: log.checkOut
        });
        setShowRegularizationModal(true);
      } else {
        setRegularizationPrefill({
          employeeId: "",
          date: log.date,
          checkIn: log.checkIn,
          checkOut: log.checkOut
        });
        setShowRegularizationModal(true);
      }
    } catch (err) {
      console.error("Error fetching employee details for regularization:", err);
      setRegularizationPrefill({
        employeeId: "",
        date: log.date,
        checkIn: log.checkIn,
        checkOut: log.checkOut
      });
      setShowRegularizationModal(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteLog = async () => {
    if (!logToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteLogMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} attendance log(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} attendance log(s) could not be deleted.`);
        }
      } else {
        await deleteLogMutation.mutateAsync(logToDelete.id);
        toast.success('Attendance log deleted successfully.');
      }
      setShowDeleteModal(false);
      setLogToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete attendance log. Please try again.');
      }
    }
  };

  // Placeholder handlers for undefined functions called in JSX
  const handleCreateLog = () => { };
  const handleEditLog = () => { };







  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Attendance Logs
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {selectedRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="attendance" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-brand" : ""}
                  aria-label={selectedRows.size > 0 ? `Export ${selectedRows.size} selected attendance logs` : "Export attendance logs"}
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleAttendanceLogsSelectedFormatExport}
                />
              </div>
            </PermissionGate>

            <PermissionGate permission="attendance" action="create">
              <div className="relative">
                <Button
                  variant="primary"
                  icon={<Upload className="h-4 w-4" />}
                  onClick={() => setShowBulkUploadModal(true)}
                  className="bg-brand hover:bg-brand-dark text-white flex items-center gap-2"
                >
                  Upload Attendance
                </Button>
              </div>
            </PermissionGate>
          </div>
        </div>
      </div>
      {/* KPI Stats Cards */}
      {/* KPI Card Theme (matched with overall pastel tint design system) */}
      {(() => {
        const KpiCard = ({ icon: Icon, title, value, color = 'blue', subtitle, trend }) => {
            const badgeColors = {
                blue: "bg-blue-50 text-blue-600",
                green: "bg-emerald-50 text-emerald-600",
                purple: "bg-purple-50 text-purple-600",
                red: "bg-rose-50 text-rose-600",
                amber: "bg-amber-50 text-amber-600",
            };
            const selectedBadge = badgeColors[color] || badgeColors.blue;

            return (
                <div className="bg-white rounded-2xl p-3.5 sm:p-4 border border-slate-100 shadow-2xs hover:shadow-md transition-all duration-200 flex items-center gap-3.5">
                    {Icon && (
                        <div className={`w-9 h-9 rounded-full ${selectedBadge} flex items-center justify-center shrink-0 shadow-2xs`}>
                            <Icon className="w-4.5 h-4.5" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-semibold text-slate-500 truncate tracking-wide">
                            {title}
                        </h3>
                        <div className="flex items-baseline gap-2 mt-0.5">
                            <span className="text-xl sm:text-2xl font-bold text-slate-900 leading-none">
                                {value}
                            </span>
                            {subtitle && (
                                <span className="text-[11px] font-medium text-slate-400 truncate">
                                    {subtitle}
                                </span>
                            )}
                            {trend && (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${trend.color}`}>
                                    {trend.label}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            );
        };

        return (
          <div className="p-6 pb-0 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5 sm:gap-4 mb-6">
              <KpiCard
                icon={Users}
                title="Active Staff"
                value={isLoadingStats ? "..." : statsData?.totalEmployees || 0}
                color="blue"
              />
              <KpiCard
                icon={UserCheck}
                title="Present"
                value={isLoadingStats ? "..." : statsData?.presentToday || 0}
                color="green"
              />
              <KpiCard
                icon={AlarmClock}
                title="Late Arrivals"
                value={isLoadingStats ? "..." : statsData?.lateToday || 0}
                color="amber"
              />
              <KpiCard
                icon={UserX}
                title="Absent"
                value={isLoadingStats ? "..." : statsData?.absentToday || 0}
                color="red"
              />
            </div>
          </div>
        );
      })()}
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3.5 sm:gap-4 w-full">
          {/* Search */}
          <div className="relative w-full lg:w-80 flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name, ID, email..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-xs sm:text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  // Maintain focus on search input
                  const searchInput = document.querySelector('input[placeholder="Search by name, ID, email..."]');
                  if (searchInput) {
                    setTimeout(() => searchInput.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full lg:w-auto">
            {/* Status Filter */}
            <div className="w-full sm:w-auto">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 h-[44px] border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-xs sm:text-sm cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="yet_checkout">Yet Checkout</option>
                <option value="absent">Absent</option>
              </select>
            </div>

            {/* Date Filter Range */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <DateRangeFilter
                startDate={filterStartDate}
                endDate={filterEndDate}
                onChange={setDateRange}
              />

              {/* Clear Filters */}
              {(searchTerm || filterStatus !== "all" || filterStartDate || filterEndDate) && (
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={() => {
                    resetAttendanceFilters();
                  }}
                  className="h-[44px] border-gray-200 text-gray-600 hover:bg-blue-50/30 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold flex-shrink-0"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Logs Table */}
      <div className="overflow-x-auto custom-scrollbar">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            <span className="ml-2 text-gray-600">
              Loading attendance logs...
            </span>
          </div>
        ) : (
          <table className="w-full mt-4 border-collapse min-w-[960px]">
            <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
              <tr>
                <th className="px-3 py-3 text-left w-10 min-w-[40px]">
                  <input
                    type="checkbox"
                    checked={attendanceLogs.length > 0 && selectedRows.size === attendanceLogs.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-brand focus:ring-0 focus:outline-none focus-visible:outline-none h-3 w-3"
                  />
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[220px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('employee')}
                >
                  <div className="flex items-center">
                    Employee {getSortIcon('employee')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[140px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('date')}
                >
                  <div className="flex items-center">
                    Date {getSortIcon('date')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[110px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('checkIn')}
                >
                  <div className="flex items-center">
                    Check In {getSortIcon('checkIn')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[160px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('checkOut')}
                >
                  <div className="flex items-center">
                    Check Out {getSortIcon('checkOut')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[120px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('workingHours')}
                >
                  <div className="flex items-center">
                    Working Hours {getSortIcon('workingHours')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[140px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('shift')}
                >
                  <div className="flex items-center">
                    Shift {getSortIcon('shift')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[130px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                  onClick={() => requestSort('status')}
                >
                  <div className="flex items-center">
                    Status {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[130px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {attendanceLogs.map((log) => {
                const status = log.status || 'absent';
                const statusLabel = getStatusLabel(status);
                const shift = getShiftDetails(log);

                return (
                  <tr key={log.id} className={`hover:bg-blue-50/30 ${selectedRows.has(log.id) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(log.id)}
                        onChange={() => handleSelectRow(log.id)}
                        className="rounded border-gray-300 text-brand focus:ring-0 focus:outline-none focus-visible:outline-none h-3 w-3"
                      />
                    </td>
                    <td className="px-3 py-4 overflow-hidden max-w-xs">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate capitalize" title={log.employeeName || "Unknown Employee"}>
                          {log.employeeName || "Unknown Employee"}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={log.employeeEmail || "N/A"}>
                          {log.employeeEmail || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={`Employee ID: ${log.employeeId || "N/A"}`}>
                          ID: {log.employeeId || "N/A"}
                        </div>
                        {log.attendanceSource === 'wfh_web' && (
                          <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-semibold border border-green-100">
                            <Home className="h-3 w-3" />
                            WFH
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.date ? (
                        <div className="flex flex-col">
                          <span>{formatDate(log.date, { includeTime: false })}</span>
                          <span className="text-xs text-gray-500 mt-0.5">
                            {getDayNameFromDate(log.date)}
                          </span>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col whitespace-nowrap">
                        <span>{formatTime(log.checkIn)}</span>
                        {log.checkinLate && log.checkinLateTime && (
                          <span className="text-[10px] text-red-500 font-bold">Late ({log.checkinLateTime})</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col whitespace-nowrap">
                        <span>{formatTime(log.checkOut)}</span>
                        {log.checkoutEarly && log.checkoutEarlyTime && (
                          <span className="text-[10px] text-red-500 font-bold">Early Left ({log.checkoutEarlyTime})</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-bold text-gray-700 bg-gray-50/30">
                      {log.workingHours || "N/A"}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col gap-0.5 whitespace-nowrap">
                        <span className="font-semibold text-gray-800 truncate" title={shift.type || "Regular"}>
                          {shift.type || "Regular"}
                        </span>
                        <span className="text-xs text-gray-500" title={`${formatTime(shift.startTime)} - ${formatTime(shift.endTime)}`}>
                          {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-start">
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-semibold capitalize whitespace-nowrap inline-flex items-center gap-1 ${getStatusColor(
                            status
                          )}`}
                          title={log.checkinLate && log.checkinLateTime ? `Late by ${log.checkinLateTime}` : ''}
                        >
                          {statusLabel}
                        </span>
                        {(log.isGrace || log.freeLateNum || log.graceIndex) && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300 font-bold whitespace-nowrap inline-flex items-center gap-1 shadow-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                            {getOrdinalGraceLabel(log.freeLateNum || log.graceIndex)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <div className="flex items-center justify-end space-x-0.5">
                        <PermissionGate permission="attendance" action="read">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                            className="text-brand hover:text-blue-900 !px-1.5"
                            aria-label="View attendance log details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="attendance" action="create">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenRegularizeModal(log)}
                            className="text-orange-500 hover:text-orange-800 !px-1.5"
                            aria-label="Request regularization"
                            title="Request regularization"
                          >
                            <Calendar className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="attendance" action="delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLog(log)}
                            className="text-red-500 hover:text-red-800 !px-1.5"
                            aria-label="Delete attendance log"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </td>


                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Pagination - integrated into the table card */}
        {!isLoading && (
          <PaginationControls
            currentPage={pagination?.currentPage || 1}
            totalPages={pagination?.totalPages || 1}
            totalItems={Number(pagination?.totalLogs || attendanceLogs.length || 0)}
            limit={itemsPerPage || 10}
            hasNextPage={pagination?.hasNextPage ?? ((pagination?.currentPage || 1) < (pagination?.totalPages || 1))}
            hasPrevPage={pagination?.hasPrevPage ?? ((pagination?.currentPage || 1) > 1)}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onLimitChange={(newLimit) => {
              setItemsPerPage(newLimit);
            }}
            limitOptions={[10, 25, 50, 100]}
            border={false}
          />
        )}
      </div>

      {
        !isLoading && attendanceLogs.length === 0 && (
          <div className="text-center py-12">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No attendance logs found
            </h3>
          </div>
        )
      }

      {/* Create Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">
                Create Attendance Log
              </h3>
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee
                  </label>
                  <div className="relative employee-dropdown-container">
                    {/* Display selected employee or placeholder */}
                    <div
                      onClick={() => {
                        setEmployeeSearchOpen(!employeeSearchOpen);
                        if (!employeeSearchOpen) {
                          setEmployeeSearchTerm("");
                        }
                        // Don't clear selectedEmployeeData when toggling dropdown
                      }}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 min-w-0"
                    >
                      <span className={`${formData.employee ? "text-gray-900" : "text-gray-500"} truncate flex-1 min-w-0 mr-2`} title={formData.employee && selectedEmployeeData ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""} (${selectedEmployeeData.employeeId || ""})` : formData.employee ? `Employee ID: ${formData.employee}` : "Select Employee"}>
                        {formData.employee && selectedEmployeeData
                          ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                          } (${selectedEmployeeData.employeeId || ""})`
                          : formData.employee
                            ? `Employee ID: ${formData.employee}`
                            : "Select Employee"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${employeeSearchOpen ? "transform rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {/* Dropdown with search */}
                    {employeeSearchOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={employeeSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                setEmployeeSearchTerm(value);
                              }}
                              onKeyPress={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={100}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-0 focus:outline-none focus-visible:outline-none focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {isLoadingEmployeeSearch ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                              <span>Loading employees...</span>
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => (
                              <div
                                key={employee._id}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    employee: employee._id,
                                  });
                                  // Store the selected employee data for display
                                  setSelectedEmployeeData(employee);
                                  setEmployeeSearchOpen(false);
                                  setEmployeeSearchTerm("");
                                }}
                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employee === employee._id
                                  ? "bg-blue-50"
                                  : ""
                                  }`}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.personalInfo?.firstName || ""}{" "}
                                  {employee.personalInfo?.lastName || ""}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {employee.employeeId || ""} -{" "}
                                  {employee.employmentInfo?.department || ""}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isLoadingEmployeeSearch
                      ? "Fetching employees..."
                      : dropdownEmployees.length > 0
                        ? `Found ${dropdownEmployees.length} employee(s)`
                        : "Type to search employees..."}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock In Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockIn}
                      onChange={(e) =>
                        setFormData({ ...formData, clockIn: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock Out Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockOut}
                      onChange={(e) =>
                        setFormData({ ...formData, clockOut: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setFormData({ ...formData, remarks: value });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={1500}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.remarks.length}/1500 characters
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedEmployeeData(null);
                    setEmployeeSearchOpen(false);
                    setEmployeeSearchTerm("");
                  }}
                >
                  Cancel
                </Button>
                <PermissionGate permission="attendance" action="create">
                  <Button variant="primary" onClick={handleCreateLog}>
                    Create
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Modal */}
      {
        showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Edit Attendance Log</h3>
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee
                  </label>
                  <div className="relative employee-dropdown-container">
                    {/* Display selected employee or placeholder */}
                    <div
                      onClick={() => {
                        setEmployeeSearchOpen(!employeeSearchOpen);
                        if (!employeeSearchOpen) {
                          setEmployeeSearchTerm("");
                        }
                        // Don't clear selectedEmployeeData when toggling dropdown
                      }}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 min-w-0"
                    >
                      <span className={`${formData.employee ? "text-gray-900" : "text-gray-500"} truncate flex-1 min-w-0 mr-2`} title={formData.employee && selectedEmployeeData ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""} (${selectedEmployeeData.employeeId || ""})` : formData.employee ? `Employee ID: ${formData.employee}` : "Select Employee"}>
                        {formData.employee && selectedEmployeeData
                          ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                          } (${selectedEmployeeData.employeeId || ""})`
                          : formData.employee
                            ? `Employee ID: ${formData.employee}`
                            : "Select Employee"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${employeeSearchOpen ? "transform rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {/* Dropdown with search */}
                    {employeeSearchOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={employeeSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                setEmployeeSearchTerm(value);
                              }}
                              onKeyPress={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={100}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-0 focus:outline-none focus-visible:outline-none focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {isLoadingEmployeeSearch ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                              <span>Loading employees...</span>
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => (
                              <div
                                key={employee._id}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    employee: employee._id,
                                  });
                                  // Store the selected employee data for display
                                  setSelectedEmployeeData(employee);
                                  setEmployeeSearchOpen(false);
                                  setEmployeeSearchTerm("");
                                }}
                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employee === employee._id
                                  ? "bg-blue-50"
                                  : ""
                                  }`}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.personalInfo?.firstName || ""}{" "}
                                  {employee.personalInfo?.lastName || ""}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {employee.employeeId || ""} -{" "}
                                  {employee.employmentInfo?.department || ""}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isLoadingEmployeeSearch
                      ? "Fetching employees..."
                      : dropdownEmployees.length > 0
                        ? `Found ${dropdownEmployees.length} employee(s)`
                        : "Type to search employees..."}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock In Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockIn}
                      onChange={(e) =>
                        setFormData({ ...formData, clockIn: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock Out Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockOut}
                      onChange={(e) =>
                        setFormData({ ...formData, clockOut: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setFormData({ ...formData, remarks: value });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={1500}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.remarks.length}/1500 characters
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEmployeeData(null);
                    setEmployeeSearchOpen(false);
                    setEmployeeSearchTerm("");
                  }}
                >
                  Cancel
                </Button>
                <PermissionGate permission="attendance" action="update">
                  <Button variant="primary" onClick={handleEditLog}>
                    Update
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }


      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Attendance Logs"
        showDateRange
        scopedFormatExport={{
          onExport: handleAttendanceLogsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLogToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteLog}
        title={isBulkDelete ? "Delete Attendance Logs" : "Delete Attendance Log"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected attendance logs? This action cannot be undone.`
            : "Are you sure you want to delete this attendance log? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          // Refetch attendance logs and stats after successful upload
          queryClient.invalidateQueries({ queryKey: ['attendance-logs'] });
          queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
          setShowBulkUploadModal(false);
          toast.success("Attendance logs uploaded and synchronized successfully");
        }}
        config={{
          title: 'Bulk Upload Attendance',
          subtitle: 'Import biometric or manual attendance logs using an Excel file',
          templateEndpoint: '/biometric-attendance/bulk-upload/template',
          validateEndpoint: '/biometric-attendance/bulk-upload/validate',
          uploadEndpoint: '/biometric-attendance/bulk-upload',
          templateFilename: 'attendance_bulk_upload_template.xlsx',
          dataKey: 'data',
          instructions: {
            download: [
              'Download the attendance template',
              'Fill in the record details',
              'Required: Employee Code, In Time OR Out Time'
            ],
            prepare: [
              'Use date format: YYYY-MM-DD HH:mm:ss',
              'Shift and Remarks are optional',
              'New employees will be automatically created if not found'
            ]
          }
        }}
      />
      {/* Regularization Request Modal */}
      <RegularizationRequestModal
        isOpen={showRegularizationModal}
        onClose={() => {
          setShowRegularizationModal(false);
          setRegularizationPrefill(null);
        }}
        prefilledData={regularizationPrefill}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['attendance-logs'] });
          queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
        }}
      />
    </div>
  );
};

export default AttendanceLogs;
